import type { StompClient } from "@/components/realtime/stom-client.ts";
import type { Env } from "@/env-schema";
import type { messageCallbackType, StompHeaders, StompSubscription } from "@stomp/stompjs";

import { Client } from "@stomp/stompjs";
import SockJS from "sockjs-client/dist/sockjs";

import { retrieveTokenDetailsFromLocalStorage } from "@/lib/helpers/authentication-helper.ts";

interface IActiveSubscription {
  callback: messageCallbackType;
  headers: StompHeaders;
  subscription: StompSubscription | null;
}

export default class BackofficeStompClient implements StompClient {
  private _client: Client | null;
  private connected: boolean;
  private readonly BACKOFFICE_STOMP_BASE_URL: string;

  private activeSubscriptions: Map<string, IActiveSubscription> = new Map();

  constructor(env: Pick<Env, "VITE_BACKOFFICE_BASE_URL">) {
    this._client = null;
    this.connected = false;
    this.BACKOFFICE_STOMP_BASE_URL = `${env.VITE_BACKOFFICE_BASE_URL}/stomp`;
  }

  public get isActive() {
    if (!this._client) {
      return false;
    }

    return this._client.active;
  }

  public activate() {
    if (import.meta.env.MODE === "test") {
      return;
    }

    if (!this._client) {
      const tokenDetails = retrieveTokenDetailsFromLocalStorage();

      const headers: StompHeaders = {};

      if (tokenDetails) {
        headers.Authorization = `Bearer ${tokenDetails.access_token}`;
      }

      const backofficeStompBaseUrl = this.BACKOFFICE_STOMP_BASE_URL;

      this._client = new Client({
        brokerURL: backofficeStompBaseUrl,
        connectHeaders: headers,
        webSocketFactory() {
          const target = backofficeStompBaseUrl;
          return new SockJS(target);
        },
        onConnect: () => {
          console.info("Stomp client connected");
          this.connected = true;
          this.resubscribeAll();
        },
        onDisconnect: () => {
          console.info("Stomp client disconnected");
          this.connected = false;

          for (const entry of this.activeSubscriptions.values()) {
            entry.subscription = null;
          }
        },
        onStompError: (frame) => console.error(frame),
      });
    }

    this._client.activate();
  }

  public deactivate(): Promise<void> {
    if (!this._client) {
      return Promise.resolve();
    }

    return this._client
      .deactivate()
      .then(() => {
        this._client = null;
      })
      .catch(console.error);
  }

  public subscribe(destination: string, callback: messageCallbackType, headers: StompHeaders = {}) {
    console.info(`Stomp - [Subscribe]: Registering subscription to ${destination}`);

    this.activeSubscriptions.set(destination, { callback, headers, subscription: null });

    this.trySubscribe(destination);
  }

  public unsubscribe(destination: string) {
    const entry = this.activeSubscriptions.get(destination);

    if (!entry) {
      console.warn(`Stomp - [Unsubscribe]: No known subscription for ${destination}`);

      return;
    }

    if (entry.subscription && this.connected) {
      console.info(`Stomp - [Unsubscribe]: Unsubscribing from ${destination}`);

      try {
        entry.subscription.unsubscribe();
      } catch (e) {
        console.error(e);
      }
    }

    this.activeSubscriptions.delete(destination);
  }

  private trySubscribe(destination: string) {
    const entry = this.activeSubscriptions.get(destination);

    if (!entry) {
      return;
    }

    if (!this._client || !this.connected) {
      console.warn(`Stomp - [Subscribe]: Not connected yet, ${destination} will be (re)subscribed on next connect`);

      return;
    }

    console.info(`Stomp - [Subscribe]: Subscribing to ${destination}`);

    entry.subscription = this._client.subscribe(destination, entry.callback, entry.headers);
  }

  private resubscribeAll() {
    if (!this.connected) {
      return;
    }

    for (const destination of this.activeSubscriptions.keys()) {
      this.trySubscribe(destination);
    }
  }
}
